import { FC, useMemo } from 'react';
import { getUser, isWeaveUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import {
  usePopoverDialog,
  PopoverDialog,
  Avatar,
  Text,
  Button,
  SearchField,
  useFormField,
  useControlledField,
  useDebouncedValue,
  RadioField,
  useListboxState,
  Listbox,
  ListboxOption,
} from '@frontend/design-system';
import { taskCenterTrackingIds } from '../../../tracking-ids';
import { getFullName } from '../../../utils';
import { popoverStyle } from '../task-person-filter.styles';
import {
  buttonStyle,
  listboxStyle,
  optionStyle,
  radioFieldStyle,
  radioStyle,
  searchFieldStyle,
  selfUserContainer,
} from './task-assignee-filters.styles';

interface TaskAssigneeFilterProps {
  setValue: (value: string) => void;
  selectedValue: string;
  getDialogProps: ReturnType<typeof usePopoverDialog>['getDialogProps'];
}

export const TaskAssigneeFilter: FC<TaskAssigneeFilterProps> = ({ setValue, selectedValue, getDialogProps }) => {
  const { t } = useTranslation('task-center');
  const user = getUser();
  const userId = user?.userID;
  const { assignees } = useTaskCenterStore(['assignees']);
  const { onSelect, value, labelId } = useListboxState(selectedValue);
  const weaveUser = isWeaveUser();

  const selfUserName = getFullName(user);

  const otherAssignees = useMemo(() => {
    return assignees.filter((assignee) => assignee.userId !== userId);
  }, [assignees, userId]);

  const searchFieldProps = useFormField({ type: 'text' });
  const debouncedSearchValue = useDebouncedValue(searchFieldProps.value);
  const radioOptionsProps = useControlledField({
    type: 'radio',
    value: value as string,
    onChange: (value: string) => onSelect(value),
  });

  const filteredAssignees = useMemo(() => {
    if (!debouncedSearchValue.trim().length) return otherAssignees;
    return otherAssignees.filter((assignee) => {
      const name = getFullName(assignee);
      return name.toLowerCase().includes(debouncedSearchValue.toLowerCase());
    });
  }, [debouncedSearchValue, otherAssignees]);

  const onClickApply = () => {
    setValue(value as string);
  };

  return (
    <PopoverDialog {...getDialogProps()} css={popoverStyle}>
      <SearchField name={t('Search Assignee')} placeholder={t('Search')} {...searchFieldProps} css={searchFieldStyle} />
      {/* @ts-expect-error Active prop does exists destructed as rest, but is not defined in component*/}
      <Listbox labelId={labelId} onSelect={onSelect} css={listboxStyle} value={value} active={false}>
        <RadioField name={t('Assignee Filter Options')} {...radioOptionsProps} css={radioStyle}>
          <RadioField.Radio value='' css={radioFieldStyle}>
            {t('All Tasks')}
          </RadioField.Radio>
          {!weaveUser ? (
            <RadioField.Radio value={userId ?? ''} css={radioFieldStyle}>
              <span css={selfUserContainer}>
                <Text>{t('{{username}} (you)', { username: selfUserName })}</Text>
              </span>
            </RadioField.Radio>
          ) : null}
        </RadioField>
        {filteredAssignees.map((user) => {
          const name = getFullName(user);

          if (user.userId)
            return (
              <ListboxOption key={user.userId} value={user.userId} css={optionStyle}>
                <Avatar size='xs' name={name} isUser /> <Text size='large'>{name}</Text>
              </ListboxOption>
            );
          return null;
        })}
      </Listbox>
      <Button
        onClick={onClickApply}
        css={buttonStyle}
        trackingId={taskCenterTrackingIds.filters.applyAssigneeFilterButton}
      >
        {t('Apply')}
      </Button>
    </PopoverDialog>
  );
};
